/**
 * @file src/api/sessionReportApi.js
 * @description File what became of a room session, once, on the way out.
 *
 * BEST-EFFORT BY CONSTRUCTION. Nothing in the room waits for this, nothing reads
 * its result, and a session that never reports simply moves no signal — which is
 * the only honest shape available, because the moment this most needs to be sent
 * is the moment the tab is closing. A report that mattered would be a report
 * whose absence had to be detected, and there is nothing in a RAM-only relay
 * that could detect it.
 *
 * ONE transport: `fetch` with `keepalive: true`. The browser finishes a keepalive
 * request after the document is gone, which is the whole requirement — a
 * departure that only filed itself when the user politely clicked Leave would
 * miss precisely the exits the signal exists to notice.
 *
 * `sendBeacon` was the obvious choice and is the wrong one HERE, for two reasons
 * that compound. It cannot carry an `Authorization` header, so a JSON beacon to
 * another origin is not a simple request and needs a preflight — which browsers
 * routinely drop once the document is unloading. And it returns `true` for
 * merely QUEUEING the request, so a beacon that is subsequently discarded still
 * reports success, and any code that treated that as sent would skip its own
 * fallback exactly when the fallback was needed. Keepalive fetch carries the
 * token, reports a real status, and has none of that.
 *
 * Authenticated, unlike anything the relay carries: the relay's `userId` is
 * self-asserted at `sync:register`, so a report sent that way would be a claim by
 * an anonymous socket about named people. This one is a claim by an account.
 */
import { fetchJsonFromApi } from './httpClient.js';

/**
 * @param {string} grantId
 * @param {{outcome: 'completed'|'leftEarly', activeMs: number, sawPlatformIssue: boolean,
 *          peers: Array<{userId: string, outcome: 'completed'|'leftEarly'|'unknown'}>}} report
 * @param {string|null} authToken a token the caller ALREADY holds. Never resolved here: this is
 *   called from `pagehide`, and any await before `fetch` means the document dies with the request
 *   still unsent. Null is fine and ordinary — the session cookie authenticates instead, which is
 *   what mycelium accepts anyway.
 * @returns {Promise<boolean>} whether the request was accepted — NOT whether anything was recorded.
 */
export function sendSessionReport(grantId, report, authToken = null) {
  if (!grantId || !report?.outcome) return Promise.resolve(false);

  try {
    const response = fetchJsonFromApi(`/rooms/${encodeURIComponent(grantId)}/session-report`, {
      method: 'POST',
      authToken,
      credentials: 'include',
      // The reason this works from `pagehide` at all. Without it the request is
      // cancelled with the document and the commonest early exit — closing the
      // tab — reports nothing.
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report)
    });
    // The request is in flight by now; the promise is only for a caller that
    // wants the outcome. Nothing in the room does.
    return response.then((r) => Boolean(r?.ok)).catch(() => false);
  } catch {
    return Promise.resolve(false);
  }
}
